import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, Share2, Facebook, Twitter, Linkedin, Home, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ScheduleConsultationDialog } from "@/components/ScheduleConsultationDialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  category: string;
  author_name: string;
  author_title?: string;
  read_time: string;
  hero_image: string;
  created_at: string;
}

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [recentPosts, setRecentPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
    window.scrollTo(0, 0);
  }, [slug]);

  const fetchPost = async () => {
    try {
      const { data: postData, error: postError } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', slug)
        .eq('published', true)
        .maybeSingle();

      if (postError) throw postError;
      
      if (!postData) {
        navigate('/blog');
        return;
      }

      setPost(postData);

      // Fetch recent posts
      const { data: recentData, error: recentError } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('published', true)
        .neq('slug', slug)
        .order('created_at', { ascending: false })
        .limit(3);

      if (recentError) throw recentError;
      setRecentPosts(recentData || []);
    } catch (error) {
      console.error('Error fetching post:', error);
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  const shareUrl = window.location.href;
  const shareTitle = post.title;

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "image": `https://northquestmarketing.co.uk${post.hero_image}`,
    "author": {
      "@type": "Organization",
      "name": post.author_name
    },
    "publisher": {
      "@type": "Organization",
      "name": "Northquest Marketing",
      "logo": {
        "@type": "ImageObject",
        "url": "https://northquestmarketing.co.uk/logo.svg"
      }
    },
    "datePublished": post.created_at,
    "dateModified": post.created_at,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": shareUrl
    },
    "articleSection": post.category,
    "wordCount": post.content.split(/\s+/).length
  };

  const handleShare = (platform: string) => {
    let url = '';
    switch (platform) {
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(shareUrl);
        toast({
          title: "Link copied!",
          description: "The blog post link has been copied to your clipboard.",
        });
        return;
    }
    window.open(url, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{post.title} | Northquest Marketing</title>
        <meta name="title" content={`${post.title} | Northquest Marketing`} />
        <meta name="description" content={post.excerpt} />
        <meta name="author" content={post.author_name} />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={`https://northquestmarketing.co.uk${post.hero_image}`} />
        <meta property="og:site_name" content="Northquest Marketing" />
        <meta property="article:published_time" content={post.created_at} />
        <meta property="article:author" content={post.author_name} />
        <meta property="article:section" content={post.category} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={shareUrl} />
        <meta property="twitter:title" content={post.title} />
        <meta property="twitter:description" content={post.excerpt} />
        <meta property="twitter:image" content={`https://northquestmarketing.co.uk${post.hero_image}`} />

        {/* Canonical URL */}
        <link rel="canonical" href={shareUrl} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <Navigation />
      
      <main className="flex-1 pt-24 md:pt-32 pb-12 md:pb-20">
        <article className="container mx-auto px-4" itemScope itemType="https://schema.org/BlogPosting">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumbs for SEO */}
            <nav className="mb-6" aria-label="Breadcrumb" itemScope itemType="https://schema.org/BreadcrumbList">
              <ol className="flex items-center gap-2 text-sm text-muted-foreground">
                <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                  <Link to="/" className="hover:text-foreground transition-colors flex items-center gap-1" itemProp="item">
                    <Home size={16} />
                    <span itemProp="name">Home</span>
                  </Link>
                  <meta itemProp="position" content="1" />
                </li>
                <ChevronRight size={16} />
                <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                  <Link to="/blog" className="hover:text-foreground transition-colors" itemProp="item">
                    <span itemProp="name">Blog</span>
                  </Link>
                  <meta itemProp="position" content="2" />
                </li>
                <ChevronRight size={16} />
                <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                  <span className="text-foreground" itemProp="name">{post.title}</span>
                  <meta itemProp="position" content="3" />
                </li>
              </ol>
            </nav>

            {/* Back Button */}
            <Link to="/blog">
              <Button variant="ghost" className="mb-8">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Blog
              </Button>
            </Link>

            {/* Featured Image */}
            <div className="mb-8 rounded-2xl overflow-hidden">
              <img 
                src={post.hero_image} 
                alt={post.title}
                className="w-full h-auto object-cover"
                itemProp="image"
                loading="eager"
                fetchPriority="high"
              />
            </div>

            {/* Article Header */}
            <header className="mb-8 md:mb-12">
              <Badge variant="secondary" className="mb-4" itemProp="articleSection">
                {post.category}
              </Badge>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 leading-tight" itemProp="headline">
                {post.title}
              </h1>
              
              <div className="flex flex-wrap items-center gap-4 md:gap-6 text-sm text-muted-foreground mb-6">
                <div className="flex items-center gap-2">
                  <Calendar size={16} />
                  <time dateTime={post.created_at} itemProp="datePublished">
                    {new Date(post.created_at).toLocaleDateString('en-GB', { 
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </time>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={16} />
                  <span itemProp="timeRequired">{post.read_time}</span>
                </div>
                <div className="flex items-center gap-2" itemProp="author" itemScope itemType="https://schema.org/Organization">
                  By <span itemProp="name">{post.author_name}</span>
                </div>
              </div>

              <p className="text-lg text-muted-foreground mb-6" itemProp="description">
                {post.excerpt}
              </p>

              {/* Share Buttons */}
              <div className="flex items-center gap-3 pt-6 border-t">
                <span className="text-sm font-medium">Share:</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleShare('facebook')}
                  className="gap-2"
                >
                  <Facebook size={16} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleShare('twitter')}
                  className="gap-2"
                >
                  <Twitter size={16} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleShare('linkedin')}
                  className="gap-2"
                >
                  <Linkedin size={16} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleShare('copy')}
                  className="gap-2"
                >
                  <Share2 size={16} />
                  Copy Link
                </Button>
              </div>
            </header>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none mb-12 article-content" itemProp="articleBody">
              <ReactMarkdown 
                rehypePlugins={[rehypeRaw]}
                components={{
                  h1: ({node, ...props}) => <h1 className="text-3xl md:text-4xl font-bold mb-6 mt-8" {...props} />,
                  h2: ({node, ...props}) => <h2 className="text-2xl md:text-3xl font-bold mb-4 mt-8" {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-xl md:text-2xl font-semibold mb-3 mt-6" {...props} />,
                  h4: ({node, ...props}) => <h4 className="text-lg md:text-xl font-semibold mb-3 mt-4" {...props} />,
                  p: ({node, ...props}) => <p className="text-base md:text-lg leading-relaxed mb-4" {...props} />,
                  ul: ({node, ...props}) => <ul className="list-disc mb-6 ml-6 space-y-2" {...props} />,
                  ol: ({node, ...props}) => <ol className="list-decimal mb-6 ml-6 space-y-2" {...props} />,
                  li: ({node, ...props}) => <li className="text-base md:text-lg leading-relaxed" {...props} />,
                  strong: ({node, ...props}) => <strong className="font-semibold text-foreground" {...props} />,
                  em: ({node, ...props}) => <em className="italic" {...props} />,
                  a: ({node, ...props}) => <a className="text-primary underline hover:text-primary/80 transition-colors" {...props} />,
                  blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-accent pl-6 py-2 my-6 italic text-muted-foreground" {...props} />,
                  code: ({node, ...props}) => <code className="bg-muted px-2 py-1 rounded text-sm font-mono" {...props} />,
                }}
              >
                {post.content}
              </ReactMarkdown>
            </div>


            {/* CTA Section */}
            <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-none shadow-lg mb-12">
              <CardContent className="p-8 md:p-12 text-center">
                <h3 className="text-2xl md:text-3xl font-bold mb-4">
                  Ready to Implement These Strategies?
                </h3>
                <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                  Let's discuss how we can help you apply these insights to grow your business and achieve your marketing goals.
                </p>
                <ScheduleConsultationDialog
                  buttonText="Schedule a Free Consultation"
                  buttonSize="lg"
                  buttonClassName="bg-accent text-accent-foreground hover:bg-accent/90"
                />
              </CardContent>
            </Card>

            {/* Related Posts */}
            {recentPosts.length > 0 && (
              <div className="mt-16">
                <h3 className="text-2xl md:text-3xl font-bold mb-8">Related Articles</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {recentPosts.map((relatedPost) => (
                    <Card key={relatedPost.id} className="group hover:shadow-lg transition-all">
                      <CardContent className="p-6">
                        <Badge variant="secondary" className="mb-3 text-xs">
                          {relatedPost.category}
                        </Badge>
                        <h4 className="font-bold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                          {relatedPost.title}
                        </h4>
                        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                          {relatedPost.excerpt}
                        </p>
                        <Link to={`/blog/${relatedPost.slug}`}>
                          <Button variant="ghost" size="sm">
                            Read More →
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPost;
